import React, { useState, useEffect, useMemo, ReactElement } from 'react';
import { PieChartData } from '@Types/nivo';
import Notification from '@Common/Notification';
import { OVERVIEW_DEFAULT_TABS } from '@Constants/constants';
import * as types from '@Constants/actionTypes';
import { actions as compDashboardActions } from '@Actions/companyDashboardActions';
import { actions as locationDashboardActions } from '@Actions/locationDashboardActions';
import HeaderTile from './Tiles/HeaderTile';
import ByAttributeTile from './Tiles/ByAttributeTile';
import { useDispatch } from 'react-redux';
import { actions as overviewActions } from '@Actions/companyDashboardActions';
import { LocationDetailData, LocationSummaryData } from '../types';
import LocationsList from './LocationsList';
import LocationDetailTile from './Tiles/LocationDetailTile';
import './Openings.scss';

interface OpeningsProps {
    projectIds: number[];
}

const Openings = ({ projectIds }: OpeningsProps): ReactElement => {
    const dispatch = useDispatch();
    const [openingsByBrand, setOpeningsByBrand] = useState<Array<PieChartData>>([]);
    const [openingsByArea, setOpeningsByArea] = useState<Array<PieChartData>>([]);
    const [openingsByRegion, setOpeningsByRegion] = useState<Array<PieChartData>>([]);
    const [openingsByType, setOpeningsByType] = useState<Array<PieChartData>>([]);
    const [openingsByMgmtCo, setOpeningsByMgmtCo] = useState<Array<PieChartData>>([]);
    const [locations, setLocations] = useState<Array<LocationSummaryData>>([]);
    const [activeLocationDetail, setActiveLocationDetail] = useState<LocationDetailData | null>(null);

    const loadTab = async (id: number) => dispatch(compDashboardActions.loadTab(id));
    const loadDetail = async (locationKey: string) => dispatch(locationDashboardActions.loadDetailsFilters(locationKey));

    const projectIdsKey = useMemo(() => (projectIds && projectIds.length ? projectIds.slice().join(',') : ''), [projectIds]);

    const loadOpeningsData = async () => {
        try {
            const response = await loadTab(OVERVIEW_DEFAULT_TABS.Openings.id);
            if (response.type === types.GET_TAB_SUCCESS) {
               const locationsFromResponse =
                    response &&
                    response.payload &&
                    Array.isArray(response.payload.locations)
                        ? response.payload.locations
                        : [];
                setLocations(locationsFromResponse);
            } else if (response.type === types.GET_TAB_FAILURE) {
                const errorMessage =
                    (response &&
                        response.payload &&
                        response.payload.message) ||
                    'An error occurred while loading openings data.';
                Notification('error', 'Error', errorMessage);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            // Avoid API call if projectIds is empty
            if (!projectIds || projectIds.length === 0) {
                // Clear the state when no projects are selected
                setOpeningsByBrand([]);
                setOpeningsByArea([]);
                setOpeningsByRegion([]);
                setOpeningsByType([]);
                setOpeningsByMgmtCo([]);
                setLocations([]);
                setActiveLocationDetail(null);
                return;
            }

            try {
                const result = await dispatch(overviewActions.loadLocationsByProjects(projectIds));
                const data = result.payload || {};

                setOpeningsByBrand(data.brand || []);
                setOpeningsByArea(data.area || []);
                setOpeningsByRegion(data.region || []);
                setOpeningsByType(data.type || []);
                setOpeningsByMgmtCo(data.managementCompany || []);
                loadOpeningsData();
            } catch (error) {
                Notification('error', 'An Error Occurred', error);
            }        };

        fetchData();
    }, [dispatch, projectIdsKey]);

    const onLocationChange = async (id: number) => {
        // TODO: as this continues to become clearer we may require more location data but for now this existing endpoint will do
        try {
            const location = locations.find(loc => loc.id === id);
            if (!location) {
                setActiveLocationDetail(null);
                return;
            }

            const response = await loadDetail(location.locationKey);
            if (response.type === types.LOAD_LOCATION_DASHBOARD_DETAILS_SUCCESS) {
                setActiveLocationDetail(response.payload.location);
            } else if (response.type === types.LOAD_LOCATION_DASHBOARD_DETAILS_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    }

    return (
        <div className='overview-openings'>
            <div className='row'>
                <div className='col-md-6 col-lg-2'>
                    <HeaderTile title='Opened in past 90 days' text={'56'}></HeaderTile>
                </div>
                <div className='col-md-12 col-lg-6 col-xl-6'>
                    <HeaderTile title='Openings by Month'></HeaderTile>
                </div>
                <div className='col-md-6 col-lg-4'>
                    <HeaderTile title='Milestones Completed'></HeaderTile>
                </div>
            </div>

            <div className='row'>
                <div className='col-sm-12 section-header section-header'>Number of Location/Teams by Attribute</div>

                <div className='col-sm-12 by-attribute-tiles'>
                    <ByAttributeTile title='Brand' data={openingsByBrand} />
                    <ByAttributeTile title='Area' data={openingsByArea} />
                    <ByAttributeTile title='Region' data={openingsByRegion} />
                    <ByAttributeTile title='Type' data={openingsByType} />
                    <ByAttributeTile title='Mgmt Co' data={openingsByMgmtCo} />
                </div>
            </div>

            <div className='row'>
                <div className='col-sm-12 section-header'>
                    Locations
                </div>
            </div>

            <div className='row'>
                <div className='col-sm-8'>
                    <LocationsList locations={locations} onLocationChange={onLocationChange}></LocationsList>
                </div>

                <div className='col-sm-4'>
                    {activeLocationDetail ? (
                        <LocationDetailTile location={activeLocationDetail}></LocationDetailTile>
                    ) : (
                        <div className='location-detail-placeholder'>Select a location to view details</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Openings;
